/**
 * Session cache for the hunting-buddies hub and in-the-woods count.
 * Callers hydrate UI synchronously, then revalidate when stale.
 */

export const USER_BUDDIES_HUB_KEY = 'user:buddies-hub';
export const USER_IN_THE_STAND_KEY = 'user:in-the-stand';
export const BUDDIES_STALE_MS = 5 * 60 * 1000;

function readEntry(key, isValid) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !isValid(parsed.data)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function getCachedEntry(key, isValid, { allowStale = true, now = Date.now() } = {}) {
  const entry = readEntry(key, isValid);
  if (!entry) return null;
  if (!allowStale && now - Number(entry.ts || 0) > BUDDIES_STALE_MS) return null;
  return entry.data;
}

function isCachedEntryFresh(key, isValid, now = Date.now()) {
  const entry = readEntry(key, isValid);
  if (!entry) return false;
  return now - Number(entry.ts || 0) <= BUDDIES_STALE_MS;
}

function writeEntry(key, data) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // sessionStorage may be unavailable
  }
}

export function isValidBuddiesHub(data) {
  return (
    Boolean(data) &&
    Array.isArray(data.buddies) &&
    Boolean(data.requests) &&
    Array.isArray(data.requests.incoming) &&
    Array.isArray(data.requests.outgoing) &&
    Array.isArray(data.buddiesWithProfiles)
  );
}

export function getCachedBuddiesHub(options) {
  return getCachedEntry(USER_BUDDIES_HUB_KEY, isValidBuddiesHub, options);
}

export function isCachedBuddiesHubFresh(now = Date.now()) {
  return isCachedEntryFresh(USER_BUDDIES_HUB_KEY, isValidBuddiesHub, now);
}

export function setCachedBuddiesHub(data) {
  if (!isValidBuddiesHub(data)) return;
  writeEntry(USER_BUDDIES_HUB_KEY, {
    buddies: data.buddies,
    requests: {
      incoming: data.requests.incoming,
      outgoing: data.requests.outgoing,
    },
    buddiesWithProfiles: data.buddiesWithProfiles,
  });
}

export function getCachedInTheStandCount(options) {
  return getCachedEntry(USER_IN_THE_STAND_KEY, (n) => typeof n === 'number' && Number.isFinite(n), options);
}

export function setCachedInTheStandCount(count) {
  const next = Number(count);
  if (!Number.isFinite(next)) return;
  writeEntry(USER_IN_THE_STAND_KEY, next);
}

export function invalidateBuddiesCache() {
  try {
    sessionStorage.removeItem(USER_BUDDIES_HUB_KEY);
    sessionStorage.removeItem(USER_IN_THE_STAND_KEY);
  } catch {
    // ignore
  }
}
